import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { Upload, Plus, Trash2, FileText } from 'lucide-react';

import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import wizardService from '../../../services/wizardService';

const EXAMPLE_TYPES = [
  {
    id: 'classification',
    name: 'Classification',
    description: 'Text classification and categorization tasks',
    sampleData: {
      prompt: 'Classify the following text as positive, negative, or neutral sentiment:',
      role: 'user',
      testCases: [
        { input: 'I love this product! It works perfectly.', expected: 'positive' },
        { input: 'This is terrible, worst purchase ever.', expected: 'negative' },
        { input: 'The product is okay, nothing special.', expected: 'neutral' }
      ]
    }
  },
  {
    id: 'summarization',
    name: 'Summarization',
    description: 'Text summarization and content condensation',
    sampleData: {
      prompt: 'Summarize the following text in 2-3 sentences:',
      role: 'user',
      testCases: [
        {
          input: 'Artificial intelligence (AI) is intelligence demonstrated by machines, in contrast to the natural intelligence displayed by humans and animals. Leading AI textbooks define the field as the study of "intelligent agents": any device that perceives its environment and takes actions that maximize its chance of successfully achieving its goals.',
          expected: 'AI is machine intelligence that contrasts with natural intelligence. It studies intelligent agents that perceive environments and take goal-maximizing actions.'
        }
      ]
    }
  },
  {
    id: 'structured_outputs',
    name: 'Structured Outputs',
    description: 'JSON, XML, and structured data generation',
    sampleData: {
      prompt: 'Extract the following information from the text and return as JSON: name, age, occupation',
      role: 'user',
      testCases: [
        {
          input: 'John Smith is a 35-year-old software engineer working at Tech Corp.',
          expected: '{"name": "John Smith", "age": 35, "occupation": "software engineer"}'
        }
      ]
    }
  },
  {
    id: 'web_search',
    name: 'Web Search',
    description: 'Search query understanding and results processing',
    sampleData: {
      prompt: 'Generate relevant search queries for the following topic:',
      role: 'user',
      testCases: [
        {
          input: 'Best practices for React performance optimization',
          expected: 'React performance optimization, React.memo usage, React hooks optimization, Virtual DOM performance'
        }
      ]
    }
  }
];

const MODEL_OPTIONS = [
  { provider: 'openai', models: ['gpt-4o-mini', 'gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo'] },
  { provider: 'claude', models: ['claude-3-opus', 'claude-3-sonnet', 'claude-3-haiku'] },
  { provider: 'groq', models: ['llama2-70b-4096', 'mixtral-8x7b-32768'] }
];

const TestDataCreation = ({
  wizardData,
  currentStepData,
  onStepDataChange,
  onNext,
  onPrevious,
  saving
}) => {
  const [formData, setFormData] = useState(() => {
    const initialData = currentStepData || {};
    return {
      run_name: initialData.run_name || '',
      description: initialData.description || '',
      provider: initialData.provider || 'openai',
      model: initialData.model || 'gpt-4',
      prompt: initialData.prompt || '',
      role: initialData.role || 'user',
      temperature: initialData.temperature || 0.7,
      max_tokens: initialData.max_tokens || 1000,
      top_p: initialData.top_p || 1.0,
      data: initialData.data?.length > 0 ? initialData.data : [{ input: '', expected: '' }]
    };
  });

  const [validationErrors, setValidationErrors] = useState({});
  const hasPrefilledRef = useRef(false);

  console.log('TestDataCreation: currentStepData on render:', currentStepData);
  console.log('TestDataCreation: formData on render:', formData);
  console.log('TestDataCreation: wizardData:', wizardData);
  console.log('TestDataCreation: wizardData.template:', wizardData?.template);

  // Prefill from template when template is selected
  useEffect(() => {
    console.log('TestDataCreation: useEffect triggered');
    console.log('TestDataCreation: wizardData in useEffect:', wizardData);
    console.log('TestDataCreation: hasPrefilledRef.current:', hasPrefilledRef.current);

    // Get the template from wizardData.template (not wizardData.template.template)
    const templateStepData = wizardData?.template;
    const selectedTemplate = templateStepData?.template;
    console.log('TestDataCreation: templateStepData:', templateStepData);
    console.log('TestDataCreation: selectedTemplate:', selectedTemplate);
    console.log('TestDataCreation: currentStepData:', currentStepData);

    // Only prefill if we have a template and haven't already prefilled
    if (selectedTemplate && !hasPrefilledRef.current) {
      hasPrefilledRef.current = true;
      console.log('TestDataCreation: Prefilling from template:', selectedTemplate);
      console.log('TestDataCreation: Template data:', selectedTemplate.template_data);

      // Template data is stored in template_data field
      const templateData = selectedTemplate.template_data || {};

      // Build the new form data
      const newFormData = {
        run_name: selectedTemplate.name || '',
        description: selectedTemplate.description || '',
        provider: 'openai',
        model: 'gpt-4',
        prompt: '',
        role: templateData.role || 'user',
        temperature: 0.7,
        max_tokens: 1000,
        top_p: 1.0,
        data: [{ input: '', expected: '' }]
      };

      // Handle prompt - can be in various fields
      if (templateData.prompt) {
        newFormData.prompt = templateData.prompt;
      } else if (templateData.system_prompt) {
        newFormData.prompt = templateData.system_prompt;
      } else if (templateData.user_prompt) {
        newFormData.prompt = templateData.user_prompt;
      } else if (templateData.messages && templateData.messages.length > 1) {
        // Fallback to user message from messages array
        newFormData.prompt = templateData.messages[1].content || '';
      }

      // Handle test cases - can be in various formats
      console.log('TestDataCreation: Checking for test cases...');
      console.log('TestDataCreation: templateData.test_cases:', templateData.test_cases);
      console.log('TestDataCreation: templateData.testCases:', templateData.testCases);
      console.log('TestDataCreation: templateData.test_data:', templateData.test_data);
      console.log('TestDataCreation: templateData.examples:', templateData.examples);

      if (templateData.test_cases && Array.isArray(templateData.test_cases) && templateData.test_cases.length > 0) {
        console.log('TestDataCreation: Using test_cases:', templateData.test_cases);
        newFormData.data = templateData.test_cases.filter(tc => tc.input || tc.expected);
      } else if (templateData.testCases && Array.isArray(templateData.testCases) && templateData.testCases.length > 0) {
        console.log('TestDataCreation: Using testCases:', templateData.testCases);
        newFormData.data = templateData.testCases.filter(tc => tc.input || tc.expected);
      } else if (templateData.test_data && Array.isArray(templateData.test_data) && templateData.test_data.length > 0) {
        console.log('TestDataCreation: Using test_data:', templateData.test_data);
        newFormData.data = templateData.test_data.filter(tc => tc.input || tc.expected);
      } else if (templateData.examples && Array.isArray(templateData.examples) && templateData.examples.length > 0) {
        console.log('TestDataCreation: Using examples:', templateData.examples);
        newFormData.data = templateData.examples.filter(tc => tc.input || tc.expected);
      } else {
        console.log('TestDataCreation: No test cases found in template');
        console.log('TestDataCreation: Full templateData keys:', Object.keys(templateData));
      }

      // Ensure we have at least one test case
      if (!newFormData.data || newFormData.data.length === 0) {
        console.log('TestDataCreation: No valid test cases, using default empty case');
        newFormData.data = [{ input: '', expected: '' }];
      }

      console.log('TestDataCreation: Setting form data:', newFormData);
      setFormData(newFormData);

      toast.success(`Template "${selectedTemplate.name}" loaded successfully`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardData?.template]);

  // Update formData when currentStepData changes (e.g., on wizard step navigation)
  useEffect(() => {
    console.log('TestDataCreation: useEffect - currentStepData changed:', currentStepData);
    if (currentStepData) {
      setFormData(prev => {
        const newFormData = {
          ...prev,
          ...currentStepData
        };
        // Ensure at least one test case if data is empty
        if (!newFormData.data || newFormData.data.length === 0) {
          newFormData.data = [{ input: '', expected: '' }];
        }
        return newFormData;
      });
    }
  }, [currentStepData]);

  // Load example data when example type changes
  useEffect(() => {
    const loadExampleData = async () => {
      if (!formData.exampleType) return;

      try {
        // Try to load from API first
        const response = await wizardService.getEvaluationExamples(formData.exampleType);

        if (response.success !== false && response.data) {
          const exampleData = response.data;
          setFormData(prev => ({
            ...prev,
            prompt: exampleData.prompt || prev.prompt,
            role: exampleData.role || prev.role,
            data: exampleData.testCases || prev.data
          }));
        } else {
          // Fallback to local sample data
          const exampleType = EXAMPLE_TYPES.find(type => type.id === formData.exampleType);
          if (exampleType) {
            setFormData(prev => ({
              ...prev,
              prompt: exampleType.sampleData.prompt,
              role: exampleType.sampleData.role,
              data: exampleType.sampleData.testCases
            }));
          }
        }
      } catch (error) {
        console.error('Failed to load example data:', error);
        // Use local fallback
        const exampleType = EXAMPLE_TYPES.find(type => type.id === formData.exampleType);
        if (exampleType) {
          setFormData(prev => ({
            ...prev,
            prompt: exampleType.sampleData.prompt,
            role: exampleType.sampleData.role,
            data: exampleType.sampleData.testCases
          }));
        }
      }
    };

    loadExampleData();
  }, [formData.exampleType]);

  // Validate form data
  const validateForm = () => {
    const errors = {};

    if (!formData.run_name.trim()) {
      errors.run_name = 'Run name is required';
    }

    if (!formData.prompt.trim()) {
      errors.prompt = 'Prompt is required';
    }

    if (formData.data.length === 0) {
      errors.data = 'At least one test case is required';
    }

    formData.data.forEach((testCase, index) => {
      if (!testCase.input?.trim()) {
        errors[`testCase_${index}_input`] = 'Input is required';
      }
      if (!testCase.expected?.trim()) {
        errors[`testCase_${index}_expected`] = 'Expected output is required';
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add new test case
  const addTestCase = () => {
    setFormData(prev => ({
      ...prev,
      data: [
        ...prev.data,
        { input: '', expected: '' }
      ]
    }));
  };

  // Remove test case
  const removeTestCase = (index) => {
    setFormData(prev => ({
      ...prev,
      data: prev.data.filter((_, i) => i !== index)
    }));
  };

  // Update test case
  const updateTestCase = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      data: prev.data.map((testCase, i) =>
        i === index ? { ...testCase, [field]: value } : testCase
      )
    }));
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        let testCases = [];

        if (file.name.endsWith('.json')) {
          const jsonData = JSON.parse(content);
          testCases = Array.isArray(jsonData) ? jsonData : [jsonData];
        } else if (file.name.endsWith('.csv')) {
          const lines = content.split('\n').filter(line => line.trim());
          const headers = lines[0].split(',').map(h => h.trim());

          testCases = lines.slice(1).map(line => {
            const values = line.split(',').map(v => v.trim());
            const testCase = {};
            headers.forEach((header, index) => {
              testCase[header] = values[index] || '';
            });
            return testCase;
          });
        }

        setFormData(prev => ({
          ...prev,
          data: [...prev.data, ...testCases]
        }));

        toast.success(`Imported ${testCases.length} test cases`);
      } catch (error) {
        console.error('Failed to parse file:', error);
        toast.error('Failed to parse file. Please check the format.');
      }
    };

    reader.readAsText(file);
  };

  // Handle next step
  const handleNext = () => {
    if (validateForm()) {
      // Pass the current form data to parent before proceeding
      onStepDataChange(formData);
      onNext(formData);
    } else {
      toast.error('Please fix the validation errors before proceeding');
    }
  };

  // Get available models for selected provider
  const getAvailableModels = () => {
    const providerConfig = MODEL_OPTIONS.find(p => p.provider === formData.provider);
    return providerConfig ? providerConfig.models : [];
  };

  // Get max tokens limit for the selected model
  const getModelMaxTokens = (model) => {
    const limits = {
      'gpt-4o': 4096,
      'gpt-4o-mini': 16384,
      'gpt-4-turbo': 4096,
      'gpt-4': 8192,
      'gpt-3.5-turbo': 4096,
    };

    // Check for exact match first
    if (limits[model]) {
      return limits[model];
    }

    // Check for partial matches
    for (const [modelPattern, limit] of Object.entries(limits)) {
      if (model.startsWith(modelPattern)) {
        return limit;
      }
    }

    // Default fallback
    return 4096;
  };

  return (
    <div className="p-8 space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-2">Create Test Data</h2>
        <p className="text-gray-600">
          Configure your evaluation settings and define test cases for your model.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Configuration */}
        <div className="space-y-6">
          {/* Run Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Run Name *
            </label>
            <Input
              value={formData.run_name}
              onChange={(e) => handleFieldChange('run_name', e.target.value)}
              placeholder="e.g., GPT-4 Classification Test"
              className={validationErrors.run_name ? 'border-red-500' : ''}
            />
            {validationErrors.run_name && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.run_name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Optional description for your evaluation run"
              rows={3}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
            />
          </div>

          {/* Model Configuration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Provider
              </label>
              <Select
                value={formData.provider}
                onValueChange={(value) => {
                  handleFieldChange('provider', value);
                  // Reset model when provider changes
                  const newModels = MODEL_OPTIONS.find(p => p.provider === value)?.models || [];
                  if (newModels.length > 0) {
                    handleFieldChange('model', newModels[0]);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODEL_OPTIONS.map(provider => (
                    <SelectItem key={provider.provider} value={provider.provider}>
                      {provider.provider.charAt(0).toUpperCase() + provider.provider.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Model
              </label>
              <Select
                value={formData.model}
                onValueChange={(value) => handleFieldChange('model', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableModels().map(model => (
                    <SelectItem key={model} value={model}>
                      {model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Prompt Configuration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prompt *
            </label>
            <textarea
              value={formData.prompt}
              onChange={(e) => handleFieldChange('prompt', e.target.value)}
              placeholder="Enter your evaluation prompt..."
              rows={4}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${validationErrors.prompt ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {validationErrors.prompt && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.prompt}</p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <Select
              value={formData.role}
              onValueChange={(value) => handleFieldChange('role', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="system">System</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="assistant">Assistant</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Advanced Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Advanced Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Temperature: {formData.temperature}
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={formData.temperature}
                  onChange={(e) => handleFieldChange('temperature', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Tokens
                </label>
                <Input
                  type="number"
                  value={formData.max_tokens}
                  onChange={(e) => handleFieldChange('max_tokens', parseInt(e.target.value))}
                  min="1"
                  max={getModelMaxTokens(formData.model)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum: {getModelMaxTokens(formData.model).toLocaleString()} tokens for {formData.model}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Top P: {formData.top_p}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={formData.top_p}
                  onChange={(e) => handleFieldChange('top_p', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Test Cases */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Test Cases</h3>
            <div className="flex space-x-2">
              <input
                type="file"
                accept=".json,.csv"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('file-upload').click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={addTestCase}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </div>

          {validationErrors.data && (
            <p className="text-red-500 text-sm">{validationErrors.data}</p>
          )}

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {formData.data.map((testCase, index) => (
              <Card key={index}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Test Case {index + 1}</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTestCase(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Input *
                    </label>
                    <textarea
                      value={testCase.input}
                      onChange={(e) => updateTestCase(index, 'input', e.target.value)}
                      placeholder="Enter test input..."
                      rows={2}
                      className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${validationErrors[`testCase_${index}_input`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {validationErrors[`testCase_${index}_input`] && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors[`testCase_${index}_input`]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Expected Output *
                    </label>
                    <textarea
                      value={testCase.expected}
                      onChange={(e) => updateTestCase(index, 'expected', e.target.value)}
                      placeholder="Enter expected output..."
                      rows={2}
                      className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${validationErrors[`testCase_${index}_expected`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {validationErrors[`testCase_${index}_expected`] && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors[`testCase_${index}_expected`]}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {formData.data.length === 0 && (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No test cases added yet</p>
                <Button onClick={addTestCase}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Test Case
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t">
        <Button
          variant="outline"
          onClick={onPrevious}
        >
          Previous
        </Button>

        <Button
          onClick={handleNext}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Continue'}
        </Button>
      </div>
    </div>
  );
};

export default TestDataCreation;
